/**
 * Date-preset codec.
 *
 * `getDateRange` is the single source of truth for what each named preset means.
 * It is agnostic — it knows presets, nothing about URLs, PISA, or filters.
 *
 * `now` is injectable ONLY for deterministic tests (fixed clock). Production
 * always uses the default `new Date()`.
 */

import { addDays, startOfDay, subDays } from 'date-fns'

export const DATE_PRESET_VALUES = [
  'today',
  'yesterday',
  'last7',
  'last30',
  'last90',
  'next7',
  'next30',
  'next90',
] as const

export type DatePreset = (typeof DATE_PRESET_VALUES)[number]

/**
 * Preset key → absolute `gte`/`lte` pair, computed relative to `now`.
 * Returns `{}` for any non-preset value — this is the guard the provider uses
 * to distinguish presets from ordinary filter values.
 */
export const getDateRange = (
  preset: string,
  now: Date = new Date(),
): { gteValue?: string; lteValue?: string } => {
  // Both bounds are day-quantized to midnight, never the live instant. A live
  // `now` would change on every render, giving the Scrivito transform a new
  // query identity each time and forcing a full refetch (table flash + scroll
  // reset) on load-more. Quantizing keeps the expanded range stable across all
  // renders within a day while staying relative to "today" for bookmarks.
  // date-fns addDays/subDays are calendar-aware, so bounds stay at midnight
  // across DST transitions (raw ms arithmetic would drift by an hour).
  const today = startOfDay(now)
  const tomorrow = addDays(today, 1)

  switch (preset) {
    case 'today':
      return {
        gteValue: today.toISOString(),
        lteValue: tomorrow.toISOString(),
      }
    case 'yesterday':
      return {
        gteValue: subDays(today, 1).toISOString(),
        lteValue: today.toISOString(),
      }
    case 'last7':
      return {
        gteValue: subDays(today, 7).toISOString(),
        lteValue: tomorrow.toISOString(),
      }
    case 'last30':
      return {
        gteValue: subDays(today, 30).toISOString(),
        lteValue: tomorrow.toISOString(),
      }
    case 'last90':
      return {
        gteValue: subDays(today, 90).toISOString(),
        lteValue: tomorrow.toISOString(),
      }
    case 'next7':
      return {
        gteValue: today.toISOString(),
        lteValue: addDays(today, 7).toISOString(),
      }
    case 'next30':
      return {
        gteValue: today.toISOString(),
        lteValue: addDays(today, 30).toISOString(),
      }
    case 'next90':
      return {
        gteValue: today.toISOString(),
        lteValue: addDays(today, 90).toISOString(),
      }
    default:
      return {}
  }
}
